<template>
  <v-card v-if="!submitted" class="feedback-card mt-4" elevation="2">
    <v-card-title class="text-h6">Are you satisfied with the face swap result?</v-card-title>
    <v-card-text class="pb-4">
      <!-- 评分区域 -->
      <div class="rating-section">
        <v-rating
          v-model="ratingNumber"
          color="warning"
          hover
          size="x-large"
          :length="5"
          class="mb-2"
        />
        <p v-if="ratingNumber > 0" class="text-caption text-grey-darken-1">
          Your rating: {{ ratingNumber }} star{{ ratingNumber > 1 ? 's' : '' }}
        </p>
      </div>

      <!-- 评论框(可选) -->
      <v-textarea
        v-model="comment"
        label="Tell us about your experience (optional)"
        placeholder="Share your thoughts and suggestions..."
        rows="3"
        variant="outlined"
        class="mt-4"
        no-resize
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="!canSubmit"
        :loading="submitting"
        color="primary"
        size="large"
        @click="submitFeedback"
      >
        Submit Feedback
      </v-btn>
    </v-card-actions>
  </v-card>

  <!-- 提交成功提示 -->
  <v-fade-transition>
    <v-alert
      v-if="submitted"
      type="success"
      variant="tonal"
      class="feedback-success mt-4"
      prominent
    >
      <template #prepend>
        <v-icon size="40">mdi-check-circle</v-icon>
      </template>
      <div class="text-h6">Thank you for your feedback!</div>
      <div class="text-body-2 mt-1">Your opinion helps us improve our product</div>
    </v-alert>
  </v-fade-transition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import api from '~/api'
import { useCommStore } from '~/composables/useCommStore'

interface Props {
  taskId?: string
  orderId?: string
  resultUrl?: string
  autoFillEmail?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  taskId: '',
  orderId: '',
  resultUrl: '',
  autoFillEmail: true
})

const emit = defineEmits<{
  submitted: [feedbackId: string]
  close: []
}>()

const commStore = useCommStore()

const ratingNumber = ref<number>(0)
const comment = ref<string>('')
const submitting = ref<boolean>(false)
const submitted = ref<boolean>(false)

const userEmail = computed(() => {
  if (props.autoFillEmail && commStore.userInfo?.email) {
    return commStore.userInfo.email
  }
  return ''
})

const canSubmit = computed(() => {
  return ratingNumber.value > 0 && !submitting.value && !submitted.value
})

const submitFeedback = async () => {
  if (!canSubmit.value) {
    return
  }

  submitting.value = true
  try {
    // 用户的评论内容
    let feedbackContent = comment.value.trim() || 'No comment'

    // 附加任务信息和评分,使用换行分隔
    const rating = ratingNumber.value || 0
    const stars = '⭐'.repeat(rating)

    feedbackContent += `\n\n========== SYSTEM INFO ==========\nRating: ${stars} (${rating}/5)\nTask ID: ${props.taskId}\nOrder ID: ${props.orderId}\n=================================`

    const result = await api.feedback(
      feedbackContent,
      props.resultUrl || '', // 结果URL作为附件
      userEmail.value,
      String(rating), // rating as string
      'v2_faceswap_result' // source 固定值
    )

    submitted.value = true

    // 触发提交成功事件
    if (result?.data?.feedback_id) {
      emit('submitted', result.data.feedback_id)
    }

    // 5秒后组件消失(不是重置,因为一次换脸只能提交一次反馈)
    setTimeout(() => {
      emit('close')
    }, 5000)
  } catch (error) {
    console.error('反馈提交失败:', error)
    // 可以在这里添加错误提示
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.feedback-card {
  max-width: 740px;
  margin: 0 auto;
  background-color: rgba(38, 38, 38, 1) !important;
}

.rating-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
}

.feedback-success {
  max-width: 740px;
  margin: 0 auto;
}

/* 修复输入框样式 */
:deep(.v-textarea .v-field) {
  border-radius: 12px !important;
}

:deep(.v-textarea .v-field__outline) {
  border-radius: 12px !important;
}

:deep(.v-textarea .v-field__field) {
  padding: 12px 16px;
}

/* 修复 label 被遮挡问题 */
:deep(.v-textarea .v-field__outline__start) {
  border-radius: 12px 0 0 12px !important;
}

:deep(.v-textarea .v-field__outline__end) {
  border-radius: 0 12px 12px 0 !important;
}

/* 修复 label 被遮挡的关键样式 */
:deep(.v-textarea .v-field__overlay) {
  overflow: visible !important;
}

/* 修复包含 v-field 的容器的 overflow 问题 */
:deep(.v-textarea .v-input__control) {
  overflow: visible !important;
}

:deep(.v-textarea .v-field) {
  overflow: visible !important;
}

/* 给 label 添加背景色,防止被边框遮挡 */
:deep(.v-textarea .v-label) {
  background-color: transparent;
  padding: 0 4px;
}

:deep(.v-textarea .v-field--focused .v-label),
:deep(.v-textarea .v-field--active .v-label) {
  background-color: transparent;
}
</style>
